'use client';

/* eslint-disable react-you-might-not-need-an-effect/no-adjust-state-on-prop-change */
import { c as _c } from "react/compiler-runtime";
import { Content as TooltipContentPrimitive, Portal as TooltipPortalPrimitive, Provider as TooltipProviderPrimitive, Root as TooltipRootPrimitive, Trigger as TooltipTriggerPrimitive } from '@radix-ui/react-tooltip';
import type { ComponentPropsWithoutRef, ComponentRef, Dispatch, Ref, SetStateAction } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
interface TooltipContextValue {
  readonly isControlled: boolean;
  readonly isTouchDevice: boolean;
  readonly setOpenState: Dispatch<SetStateAction<boolean>>;
}
const TOUCH_POINTER_QUERIES = ['(pointer: coarse)', '(hover: none)'] as const;
const createMediaQueryLists = (queries: readonly string[]): MediaQueryList[] => {
  if (globalThis.window === undefined || typeof globalThis.matchMedia !== 'function') {
    return [];
  }
  return queries.map(query => {
    try {
      return globalThis.matchMedia(query);
    } catch {
      return null;
    }
  }).filter((value): value is MediaQueryList => value !== null);
};
const createChangeHandler = (listener: () => void) => () => {
  listener();
};
const subscribeToMediaQueries = (lists: MediaQueryList[], listener: () => void) => {
  const mapped = lists.map(mql => {
    const handler = createChangeHandler(listener);
    mql.addEventListener('change', handler);
    return {
      mql,
      handler
    };
  });
  return () => {
    for (const {
      mql,
      handler
    } of mapped) {
      mql.removeEventListener('change', handler);
    }
  };
};
const detectTouchPreference = () => {
  const hasTouchPoints = navigator !== undefined && navigator.maxTouchPoints > 0;
  const matchesQuery = TOUCH_POINTER_QUERIES.some(query => {
    try {
      return globalThis.window !== undefined && globalThis.matchMedia(query).matches;
    } catch {
      return false;
    }
  });
  return hasTouchPoints || matchesQuery || globalThis.window !== undefined && 'ontouchstart' in globalThis;
};
const updateTouchPreference = (isControlled: boolean, setIsTouchDevice: Dispatch<SetStateAction<boolean>>, setOpenState: Dispatch<SetStateAction<boolean>>) => {
  const next = detectTouchPreference();
  setIsTouchDevice(previous => {
    if (!isControlled && previous && !next) {
      setOpenState(false);
    }
    return next;
  });
};
const TooltipTouchContext = createContext<TooltipContextValue | null>(null);
const TooltipProvider = TooltipProviderPrimitive;
type TooltipRootProperties = ComponentPropsWithoutRef<typeof TooltipRootPrimitive>;
const Tooltip = t0 => {
  const $ = _c(29);
  let children;
  let defaultOpen;
  let delayDuration;
  let onOpenChange;
  let openProperty;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      open: openProperty,
      defaultOpen,
      onOpenChange,
      delayDuration,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = defaultOpen;
    $[3] = delayDuration;
    $[4] = onOpenChange;
    $[5] = openProperty;
    $[6] = rest;
  } else {
    children = $[1];
    defaultOpen = $[2];
    delayDuration = $[3];
    onOpenChange = $[4];
    openProperty = $[5];
    rest = $[6];
  }
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [openState, setOpenState] = useState(defaultOpen ?? false);
  const isControlled = openProperty !== undefined;
  let t1;
  let t2;
  if ($[7] !== isControlled) {
    t1 = () => {
      if (globalThis.window === undefined) {
        return;
      }
      const handlePreferenceChange = () => {
        updateTouchPreference(isControlled, setIsTouchDevice, setOpenState);
      };
      handlePreferenceChange();
      const mediaQueryLists = createMediaQueryLists(TOUCH_POINTER_QUERIES);
      const unsubscribe = subscribeToMediaQueries(mediaQueryLists, handlePreferenceChange);
      return unsubscribe;
    };
    t2 = [isControlled];
    $[7] = isControlled;
    $[8] = t1;
    $[9] = t2;
  } else {
    t1 = $[8];
    t2 = $[9];
  }
  useEffect(t1, t2);
  let t3;
  if ($[10] !== isControlled || $[11] !== onOpenChange) {
    t3 = nextOpen => {
      if (!isControlled) {
        setOpenState(nextOpen);
      }
      onOpenChange?.(nextOpen);
    };
    $[10] = isControlled;
    $[11] = onOpenChange;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleOpenChange = t3;
  const resolvedDelayDuration = isTouchDevice ? 0 : delayDuration;
  let t4;
  if ($[13] !== resolvedDelayDuration) {
    t4 = resolvedDelayDuration === undefined ? {} : {
      delayDuration: resolvedDelayDuration
    };
    $[13] = resolvedDelayDuration;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const t5 = isControlled ? openProperty : openState;
  let t6;
  if ($[15] !== handleOpenChange || $[16] !== rest || $[17] !== t4 || $[18] !== t5) {
    t6 = {
      ...rest,
      ...t4,
      onOpenChange: handleOpenChange,
      open: t5
    };
    $[15] = handleOpenChange;
    $[16] = rest;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const rootProperties = t6;
  let t7;
  if ($[20] !== isControlled || $[21] !== isTouchDevice) {
    t7 = {
      isTouchDevice,
      isControlled,
      setOpenState
    };
    $[20] = isControlled;
    $[21] = isTouchDevice;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== children || $[24] !== rootProperties) {
    t8 = <TooltipRootPrimitive {...rootProperties}>{children}</TooltipRootPrimitive>;
    $[23] = children;
    $[24] = rootProperties;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t7 || $[27] !== t8) {
    t9 = <TooltipTouchContext.Provider value={t7}>{t8}</TooltipTouchContext.Provider>;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
};
type TooltipTriggerProperties = ComponentPropsWithoutRef<typeof TooltipTriggerPrimitive> & {
  ref?: Ref<ComponentRef<typeof TooltipTriggerPrimitive>> | undefined;
};
const TooltipTrigger = t0 => {
  const $ = _c(16);
  let onBlur;
  let onPointerDown;
  let properties;
  let ref;
  if ($[0] !== t0) {
    ({
      onPointerDown,
      onBlur,
      ref,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = onBlur;
    $[2] = onPointerDown;
    $[3] = properties;
    $[4] = ref;
  } else {
    onBlur = $[1];
    onPointerDown = $[2];
    properties = $[3];
    ref = $[4];
  }
  const context = useContext(TooltipTouchContext);
  let t1;
  if ($[5] !== context || $[6] !== onPointerDown) {
    t1 = event => {
      onPointerDown?.(event);
      if (!event.defaultPrevented && context?.isTouchDevice && !context.isControlled && (event.pointerType === "touch" || event.pointerType === "pen")) {
        context.setOpenState(_temp);
      }
    };
    $[5] = context;
    $[6] = onPointerDown;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const handlePointerDown = t1;
  let t2;
  if ($[8] !== context || $[9] !== onBlur) {
    t2 = event_0 => {
      onBlur?.(event_0);
      if (!event_0.defaultPrevented && context?.isTouchDevice && !context.isControlled) {
        context.setOpenState(false);
      }
    };
    $[8] = context;
    $[9] = onBlur;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const handleBlur = t2;
  let t3;
  if ($[11] !== handleBlur || $[12] !== handlePointerDown || $[13] !== properties || $[14] !== ref) {
    t3 = <TooltipTriggerPrimitive ref={ref} onPointerDown={handlePointerDown} onBlur={handleBlur} {...properties} />;
    $[11] = handleBlur;
    $[12] = handlePointerDown;
    $[13] = properties;
    $[14] = ref;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
};
TooltipTrigger.displayName = TooltipTriggerPrimitive.displayName;
type TooltipContentProperties = ComponentPropsWithoutRef<typeof TooltipContentPrimitive> & {
  ref?: Ref<ComponentRef<typeof TooltipContentPrimitive>> | undefined;
};
const TooltipContent = t0 => {
  const $ = _c(22);
  let className;
  let onEscapeKeyDown;
  let onPointerDownOutside;
  let properties;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      sideOffset: t1,
      onPointerDownOutside,
      onEscapeKeyDown,
      ref,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onEscapeKeyDown;
    $[3] = onPointerDownOutside;
    $[4] = properties;
    $[5] = ref;
    $[6] = t1;
  } else {
    className = $[1];
    onEscapeKeyDown = $[2];
    onPointerDownOutside = $[3];
    properties = $[4];
    ref = $[5];
    t1 = $[6];
  }
  const sideOffset = t1 === undefined ? 4 : t1;
  const context = useContext(TooltipTouchContext);
  let t2;
  if ($[7] !== context || $[8] !== onPointerDownOutside) {
    t2 = event => {
      onPointerDownOutside?.(event);
      if (!event.defaultPrevented && context?.isTouchDevice && !context.isControlled) {
        context.setOpenState(false);
      }
    };
    $[7] = context;
    $[8] = onPointerDownOutside;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handlePointerDownOutside = t2;
  let t3;
  if ($[10] !== context || $[11] !== onEscapeKeyDown) {
    t3 = event_0 => {
      onEscapeKeyDown?.(event_0);
      if (!event_0.defaultPrevented && context?.isTouchDevice && !context.isControlled) {
        context.setOpenState(false);
      }
    };
    $[10] = context;
    $[11] = onEscapeKeyDown;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleEscapeKeyDown = t3;
  let t4;
  if ($[13] !== className) {
    t4 = cn("z-layer-popover max-w-[250px] rounded-md border border-border/70 bg-card/90 px-3 py-2 text-sm text-foreground shadow-lg backdrop-blur-md supports-[backdrop-filter]:bg-background/55 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin] focus:outline-none", className);
    $[13] = className;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== handleEscapeKeyDown || $[16] !== handlePointerDownOutside || $[17] !== properties || $[18] !== ref || $[19] !== sideOffset || $[20] !== t4) {
    t5 = <TooltipContentPrimitive ref={ref} className={t4} sideOffset={sideOffset} onPointerDownOutside={handlePointerDownOutside} onEscapeKeyDown={handleEscapeKeyDown} {...properties} />;
    $[15] = handleEscapeKeyDown;
    $[16] = handlePointerDownOutside;
    $[17] = properties;
    $[18] = ref;
    $[19] = sideOffset;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
};
TooltipContent.displayName = TooltipContentPrimitive.displayName;
const TooltipPortal = TooltipPortalPrimitive;
export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipPortal };
function _temp(previous) {
  return !previous;
}